import {
  IWidgetConfigurationProps,
  withWidgetConfiguration,
} from "@sgwt-widget/core";
import { find, isEqual } from "lodash";
import { h } from "preact";
import { ApiBackedComponent } from "../../common/components/ApiBackedComponent";
import PieChart from "../../common/components/PieChart";
import { MakeGenericApiCall } from "../../common/generic/generic-api";
import {
  CheckReponseDataForError,
  GenericFilterBuilder,
} from "../../common/generic/generic-helper";
import { NbiDimensions } from "../../common/generic/generic-nbi-dimensions";
import { NBIKpis } from "../../common/generic/generic-nbi-domains";
import {
  IFilterItem,
  IGenericRequest,
} from "../../common/generic/generic-types";
import {
  BuildPortfolioFilters,
  GetPortfolioFilterAsofDate,
} from "../../common/portfolio/portfolio-filter";
import { IProps } from "../../common/portfolio/portfolio-types";
import { mapData } from "../utils/helper";
import { getCommonPptExport } from "../../common/export/chart-helper";
import { maxSelectedClients } from "../../common/utils/clientlistHelper";

const kpis = "nbi_ytd";
const attributes = "product_l1,product_l2";
const sortOrder = "product_l1,product_l2";

interface IState {
  data: any[];
  drillDownData: any[];
  pointIndex?: number;
}
const kpi = new NBIKpis();
const attr = new NbiDimensions();
export class NbiPerProductsClass extends ApiBackedComponent<
  IProps & IWidgetConfigurationProps,
  IState
> {
  protected child: PieChart;
  private isInitialLoad = false;

  public constructor() {
    super();
    this.setState({
      data: [],
      drillDownData: [],
    });
    this.onDrilledDown = this.onDrilledDown.bind(this);
    this.isInitialLoad = true;
  }

  public getPptExport() {
    return getCommonPptExport(
      "pie",
      this.child.getChartOptions(),
      undefined,
      true
    );
  }

  protected shouldFetchApiData(nextProps: IProps): boolean {
    return !isEqual(this.props.filters, nextProps.filters);
  }

  protected renderWithDataReady(): JSX.Element {
    return (
      <PieChart
        ref={instance => (this.child = instance)}
        data={this.state.data}
        drillDownData={this.state.drillDownData}
        onDrilledDown={this.onDrilledDown}
        pointIndex={this.state.pointIndex}
      />
    );
  }

  protected async fetchApiData(props: IProps) {
    this.initCorrelationId();
    const headers = props.headers;
    const filters = BuildPortfolioFilters(props.filters);
    const isClientMandatory = this.validateIsClientMandatory(props, filters);

    if (isClientMandatory) {
      this.setState({
        data: [],
        drillDownData: [],
      });
      this.setDataStatus("no-client");
      return;
    }
    this.setDataStatus("loading");
    this.setPointIndex(props);
    const dateFilter = GetPortfolioFilterAsofDate(props.filters);
    const genericRequest: IGenericRequest = {
      aggregationAttributes: attributes,
      date: dateFilter,
      filter: GenericFilterBuilder(filters),
      kpis: kpis,
      sort: sortOrder,
    };

    const filterClientsSelected = find(filters, {
      attribute: "clientgen_id",
    });
    const clientsCount = filterClientsSelected
      ? filterClientsSelected.value.split("+").length
      : 0;
    if (filterClientsSelected && clientsCount >= maxSelectedClients) {
      this.setState({
        data: [],
      });
      this.setDataStatus("too-many-clients");
    } else {
      const responseData = await MakeGenericApiCall(
        headers,
        "nbi",
        genericRequest,
        this.props.widgetConfiguration,
        this.state.correlationId
      );
      const errorStatus = CheckReponseDataForError(responseData);
      if (errorStatus) {
        this.setDataStatus(errorStatus);
        this.setStatusText(responseData.statusText);
        this.setState({
          data: [],
          drillDownData: [],
        });
      } else {
        const result = mapData(responseData);

        if (result.data.length !== 0) {
          this.setState({
            data: result.data,
            drillDownData: result.drillDownData,
          });
          this.setDataStatus("ready");
        } else {
          this.setState({
            data: [],
            drillDownData: [],
          });
          this.setDataStatus("no-data");
        }
      }
    }
  }

  private validateIsClientMandatory(props: IProps, filters: IFilterItem[]) {
    return (
      props.attribute &&
      attr.CLIENT_GEN_ID === props.attribute &&
      filters.filter(x => x.attribute === attr.CLIENT_GEN_ID).length === 0
    );
  }

  private setPointIndex(props: IProps) {
    if (this.isInitialLoad) {
      this.setState({
        pointIndex:
          this.props.userSelection &&
          this.props.userSelection.drilledPoint &&
          this.props.userSelection.drilledPoint.length > 0
            ? Number(this.props.userSelection.drilledPoint)
            : undefined,
      });
      this.isInitialLoad = false;
    }
  }
  private onDrilledDown(pointIndex?: number) {
    if (this.props.onConfigChanged) {
      const index =
        pointIndex !== null && pointIndex !== undefined
          ? pointIndex.toString()
          : "";
      this.props.onConfigChanged(index);
    }
  }
}
export const NbiPerProducts = withWidgetConfiguration(NbiPerProductsClass);
